import type { ReactNode } from "react";
import { BUCKETS, BUCKET_LABELS } from "../api";
import type { Balance, Bucket, Int64 } from "../api";
import { Badge, Card, SectionTitle, Skeleton, Stat } from "../components";
import { formatPercent, formatTokens, percentOf, ratio } from "../format";
import { useCardEntrance, useCountUp, useGaugeFill } from "../motion";
import { bucketStyle } from "../theme";
import styles from "./Dashboard.module.css";

/* Pictogrammes au trait, même grille de 24 et même contour que ceux de la
   navigation : un jeu d'icônes emprunté à deux sources se voit immédiatement. */
const traits = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function icone(children: ReactNode) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...traits}>
      {children}
    </svg>
  );
}

/**
 * Une icône par seau, de la même famille que les couleurs : disponible = une
 * bourse ouverte, réservé = un cadenas, consommé = une flamme, expiré = un
 * sablier. Elle ne porte aucune information que le libellé ne dise déjà —
 * c'est un repère, pas une donnée.
 */
const ICONES: Record<Bucket, ReactNode> = {
  available: icone(
    <>
      <path d="M4 8h16v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z" />
      <path d="M7.5 8V6a2.5 2.5 0 0 1 2.5-2.5h4A2.5 2.5 0 0 1 16.5 6v2" />
      <path d="M12 12.5v3.5" />
    </>,
  ),
  reserved: icone(
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" />
    </>,
  ),
  consumed: icone(
    <>
      <path d="M12 3.5c2.8 3 4.2 5.3 4.2 7a4.2 4.2 0 1 1-8.4 0c0-1.7 1.4-4 4.2-7Z" />
      <path d="M12 20.5v-3" />
    </>,
  ),
  expired: icone(
    <>
      <path d="M7 3.5h10M7 20.5h10" />
      <path d="M8 3.5v3.2c0 1.6 1.4 2.6 2.6 3.7.9.8.9 1.4 0 2.2-1.2 1.1-2.6 2.1-2.6 3.7v3.2" />
      <path d="M16 3.5v3.2c0 1.6-1.4 2.6-2.6 3.7-.9.8-.9 1.4 0 2.2 1.2 1.1 2.6 2.1 2.6 3.7v3.2" />
    </>,
  ),
};

/**
 * Une tuile de seau : le montant qui monte, la part qui se remplit, la couleur
 * du seau sur le filet, le halo et la jauge.
 *
 * Un composant à part parce que `useCountUp` est un hook : quatre tuiles écrites
 * à la main dans le parent, ce sont quatre hooks recopiés et une cinquième
 * variante le jour où un seau s'ajoute.
 */
function TuileSeau({
  bucket,
  montant,
  total,
}: {
  bucket: Bucket;
  montant: Int64;
  total: Int64;
}) {
  // L'élément reste vide dans le JSX : c'est le compteur qui écrit son texte.
  const compteur = useCountUp<HTMLSpanElement>(montant);
  const part = ratio(montant, total);

  return (
    <Stat
      data-anim="card"
      bucket={bucket}
      icon={ICONES[bucket]}
      label={BUCKET_LABELS[bucket]}
      value={<span ref={compteur} className="lde-tabular" />}
      unit="tokens"
      ratio={part}
      hint={`${formatPercent(part * 100)} du total`}
    />
  );
}

/**
 * Un segment de la barre empilée.
 *
 * La largeur vient d'un `flex-grow` proportionnel, posé une fois : c'est une
 * mise en page, elle ne s'anime pas. Seul le remplissage s'anime, en `scaleX`,
 * ce qui reste sur le compositeur.
 */
function SegmentRepartition({ bucket, part }: { bucket: Bucket; part: number }) {
  const remplissage = useGaugeFill<HTMLDivElement>(1);

  return (
    <div
      className={styles.segment}
      style={{ ...bucketStyle(bucket), flexGrow: part }}
    >
      <div ref={remplissage} className={styles.segmentRemplissage} />
    </div>
  );
}

/**
 * Silhouette des soldes pendant le premier chargement.
 *
 * Mêmes proportions et même grille que le bloc réel : les tuiles arrivent à la
 * place qu'occupaient leurs silhouettes, la page ne saute pas.
 */
export function DashboardSeauxSquelette() {
  return (
    <div className={styles.squelette}>
      <Skeleton variant="block" height="7.5rem" />
      <div className={styles.squeletteGrille}>
        {SEAUX_EN_TUILES.map((seau) => (
          <Skeleton key={seau} variant="block" height="9.5rem" />
        ))}
      </div>
      <Skeleton variant="block" height="11rem" />
    </div>
  );
}

export type DashboardSeauxProps = {
  balance: Balance;
};

/**
 * Les seaux qui ont leur tuile dans la grille : tous sauf le disponible, qui
 * est le chiffre principal juste au-dessus. L'afficher deux fois à dix pixels
 * d'écart n'apprendrait rien ; la carte de répartition, elle, garde bien les
 * quatre, parce que c'est elle qui explique le modèle.
 */
const SEAUX_EN_TUILES = BUCKETS.filter((seau) => seau !== "available");

/**
 * Le cœur de l'écran : ce qu'il reste à dépenser, où sont passés les autres
 * tokens, et la répartition.
 *
 * Le premier chiffre est celui sur lequel on agit — le disponible, ce qu'on
 * peut encore commander. Le total entré est un témoin comptable : il reste
 * visible, en contexte sous le chiffre, mais il ne déclenche aucune décision et
 * n'a pas la place d'honneur. Les quatre seaux gardent partout le même ordre
 * (`BUCKETS`) et les mêmes couleurs : c'est cette répétition, du dashboard à
 * l'historique, qui fait comprendre le grand livre sans l'expliquer.
 */
export function DashboardSeaux({ balance }: DashboardSeauxProps) {
  // Une seule entrée en cascade pour tout le bloc : le disponible, les trois
  // autres seaux, puis la carte de répartition. Le décalage donne à l'œil le
  // temps de compter les tuiles avant de les lire.
  const entree = useCardEntrance<HTMLDivElement>({ dependencies: [balance] });
  // Plus lente que les autres montées : c'est le chiffre qu'on vient voir,
  // et l'œil doit avoir le temps de le voir monter.
  const disponible = useCountUp<HTMLSpanElement>(balance.available, { duration: 1.4 });

  const parts = BUCKETS.map((seau) => ({
    seau,
    montant: balance[seau],
    part: ratio(balance[seau], balance.total),
    pourcent: percentOf(balance[seau], balance.total),
  }));

  // Description textuelle de la barre, pour qui ne voit pas les couleurs. La
  // barre elle-même est un `role="img"` : ses segments n'ont rien à annoncer.
  const descriptionBarre = parts
    .map((p) => `${BUCKET_LABELS[p.seau]} ${p.pourcent} %`)
    .join(", ");

  return (
    <section className={styles.section} aria-labelledby="titre-seaux">
      <SectionTitle id="titre-seaux">
        Votre solde
      </SectionTitle>

      <div className={styles.pile} ref={entree}>
        <Stat
          data-anim="card"
          className={styles.vedette}
          emphasis
          bucket="available"
          icon={ICONES.available}
          label="Disponible pour de nouveaux projets"
          highlight={balance.available}
          value={<span ref={disponible} className="lde-tabular" />}
          unit="tokens"
          ratio={ratio(balance.available, balance.total)}
          hint={`${formatTokens(balance.reserved)} tokens réservés sur vos projets en cours · ${formatTokens(balance.in)} reçus au total.`}
        />

        <div className={styles.grilleSeaux}>
          {SEAUX_EN_TUILES.map((seau) => (
            <TuileSeau
              key={seau}
              bucket={seau}
              montant={balance[seau]}
              total={balance.total}
            />
          ))}
        </div>

        <Card
          data-anim="card"
          title="Répartition"
          subtitle="Où sont vos tokens, en part du total."
        >
          <div
            className={styles.barre}
            role="img"
            aria-label={`Répartition des seaux : ${descriptionBarre}.`}
          >
            {parts
              // Un seau à zéro — ou passé sous zéro, ce que `ratio` ramène à
              // zéro — n'occuperait aucune largeur : le rendre poserait un
              // élément invisible de plus dans la barre.
              .filter((p) => p.part > 0)
              .map((p) => (
                <SegmentRepartition key={p.seau} bucket={p.seau} part={p.part} />
              ))}
          </div>

          <ul className={styles.legende}>
            {parts.map((p) => (
              <li key={p.seau} className={styles.legendeItem}>
                {/* Le badge teinte la pastille, le texte porte le nom du seau :
                    la couleur n'est jamais seule à dire de quoi on parle. */}
                <Badge bucket={p.seau} size="sm" dot>
                  {BUCKET_LABELS[p.seau]}
                </Badge>
                {/* Les montants sont déjà sur les tuiles au-dessus : la légende
                    ne redit que la part, ce que la barre montre. */}
                <span className={styles.legendeMontant}>
                  {formatPercent(p.pourcent)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </section>
  );
}
